import { useState, useCallback, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNotificationContext } from '../providers/NotificationProvider';
import { useSourceInventoryPolling } from './useSourceInventoryPolling';
import { useCsvDownload } from './useCsvDownload';
import { rivermeadowApi } from '../api/api';
import { NIL_UUID } from '../constants';

/**
 * Custom hook to handle vSphere source refresh with polling
 * Uses RTK Query polling via useSourceInventoryPolling hook
 */
export const useRefreshSources = ({ userOrg, onSuccess, onError }) => {
    const [sourceIds, setSourceIds] = useState([]);
    const cloudAccountDataRef = useRef(null);
    const onSuccessRef = useRef(onSuccess);
    const onErrorRef = useRef(onError);
    const shouldDownloadCSVRef = useRef(false);
    const moveGroupInfoRef = useRef(null);

    const dispatch = useDispatch();
    const { notifySuccess, notifyError, notifyWarning } = useNotificationContext();
    const sourceRefreshPolling = useSourceInventoryPolling();
    const { triggerCsvDownload, isDownloadingCsv } = useCsvDownload();

    // Keep refs updated
    useEffect(() => {
        onSuccessRef.current = onSuccess;
    }, [onSuccess]);

    useEffect(() => {
        onErrorRef.current = onError;
    }, [onError]);

    const triggerSourcesRefreshFn = sourceRefreshPolling.triggerSourcesRefresh;

    const startSourceRefresh = useCallback(
        async (refreshParams, cloudAccountData) => {
            try {
                cloudAccountDataRef.current = cloudAccountData;
                shouldDownloadCSVRef.current = refreshParams.shouldDownloadCSV || false;

                const filters = {};
                if (refreshParams.datacenter_name) filters.datacenter_name = refreshParams.datacenter_name;
                if (refreshParams.cluster_name) filters.cluster_name = refreshParams.cluster_name;
                if (refreshParams.resource_pool) filters.resource_pool = refreshParams.resource_pool;
                if (refreshParams.folder_name) filters.folder_name = refreshParams.folder_name;

                const moveGroupInfo = {};
                if (refreshParams.move_group_id) moveGroupInfo.move_group_id = refreshParams.move_group_id;
                if (refreshParams.move_group_name) moveGroupInfo.move_group_name = refreshParams.move_group_name;
                if (refreshParams.target_migration_date)
                    moveGroupInfo.target_migration_date = refreshParams.target_migration_date;
                if (refreshParams.assigned_resource_email)
                    moveGroupInfo.assigned_resource_email = refreshParams.assigned_resource_email;

                // Store move group info to check later if we need to invalidate MOVE_GROUPS cache
                moveGroupInfoRef.current = Object.keys(moveGroupInfo).length > 0 ? moveGroupInfo : null;

                // Trigger source refresh with RTK Query polling
                await triggerSourcesRefreshFn({
                    userOrg,
                    applianceId: refreshParams.applianceId,
                    filters: Object.keys(filters).length > 0 ? filters : undefined,
                    moveGroupInfo: Object.keys(moveGroupInfo).length > 0 ? moveGroupInfo : undefined,
                });

                // Show initial success notification
                notifySuccess({ description: 'vSphere sources refresh request sent' });
            } catch (error) {
                console.error('Error triggering source inventory:', error);
                notifyError({ description: 'Error sending vSphere sources refresh request' });
                onErrorRef.current?.(error);
            }
        },
        [userOrg, triggerSourcesRefreshFn, notifySuccess, notifyError],
    );

    // Handle polling completion
    const { isComplete, inventoryStatus, hasError } = sourceRefreshPolling;
    const hasNotifiedRef = useRef(false);

    // Update source IDs and show notification when complete
    useEffect(() => {
        if (isComplete && inventoryStatus && !hasNotifiedRef.current) {
            hasNotifiedRef.current = true;
            const newSourceIds = inventoryStatus.source_ids || [];
            setSourceIds(newSourceIds);

            // Show success notification
            notifySuccess({ description: 'Refresh Source Inventory completed.' });

            // Manually invalidate SOURCES tag to refresh the sources list
            const tagsToInvalidate = ['SOURCES'];

            // If a move group was involved, also invalidate MOVE_GROUPS cache
            // This ensures the newly created move group appears in the list next time
            if (moveGroupInfoRef.current && moveGroupInfoRef.current.move_group_id === NIL_UUID) {
                tagsToInvalidate.push('MOVE_GROUPS');
            }

            dispatch(rivermeadowApi.util.invalidateTags(tagsToInvalidate));

            // Call success callback with source IDs
            onSuccessRef.current?.(newSourceIds);

            // Automatically trigger CSV download if requested
            if (shouldDownloadCSVRef.current && newSourceIds.length > 0 && cloudAccountDataRef.current) {
                const cloudAccount = cloudAccountDataRef.current;
                const csvPayload = {
                    cloud_account: cloudAccount.id,
                    cloud_type: cloudAccount.type,
                    appliance_id: cloudAccount.appliance.id,
                    sources: newSourceIds,
                    migration_method: 'vm-based',
                    data_only_migration: false,
                    vdi_migration: false,
                    migration_type: 'full',
                    organization_id: userOrg,
                };
                triggerCsvDownload(csvPayload);
                shouldDownloadCSVRef.current = false; // Reset the flag
            } else if (shouldDownloadCSVRef.current && newSourceIds.length === 0) {
                notifyWarning({ description: 'No sources available to download' });
                shouldDownloadCSVRef.current = false; // Reset the flag
            }
        }
    }, [isComplete, inventoryStatus, notifySuccess, notifyWarning, dispatch, userOrg, triggerCsvDownload]);

    // Handle errors
    useEffect(() => {
        if (hasError && !hasNotifiedRef.current) {
            hasNotifiedRef.current = true;
            notifyError({ description: 'Error refreshing vSphere sources' });
            onErrorRef.current?.(sourceRefreshPolling.error);
            // Reset CSV download flag on error
            shouldDownloadCSVRef.current = false;
        }
    }, [hasError, notifyError, sourceRefreshPolling.error]);

    // Reset notification flag when starting new refresh
    useEffect(() => {
        if (sourceRefreshPolling.inventoryId) {
            hasNotifiedRef.current = false;
            // Reset sourceIds when starting a new refresh
            setSourceIds([]);
            // Reset move group info
            moveGroupInfoRef.current = null;
        }
    }, [sourceRefreshPolling.inventoryId]);

    const downloadCSV = useCallback(() => {
        if (sourceIds.length === 0 || !cloudAccountDataRef.current) {
            notifyWarning({ description: 'No sources available to download' });
            return;
        }

        const cloudAccount = cloudAccountDataRef.current;
        const csvPayload = {
            cloud_account: cloudAccount.id,
            cloud_type: cloudAccount.type,
            appliance_id: cloudAccount.appliance.id,
            sources: sourceIds,
            migration_method: 'vm-based',
            data_only_migration: false,
            vdi_migration: false,
            migration_type: 'full',
            organization_id: userOrg,
        };

        // Trigger CSV download using the proper React hook
        triggerCsvDownload(csvPayload);
    }, [sourceIds, userOrg, triggerCsvDownload, notifyWarning, cloudAccountDataRef]);

    return {
        isRefreshing: sourceRefreshPolling.isLoading,
        isDownloadingCsv,
        sourceIds,
        startSourceRefresh,
        downloadCSV,
        stopPolling: sourceRefreshPolling.reset,
    };
};
